"use client";

import { useState } from "react";
import {
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import { sorted, standardDeviation, sum } from "@/lib/stats/descriptive";
import {
  type DeviationReport,
  deviationReport,
} from "@/lib/tool-kit/stats-descriptive/deviation";
import {
  formatNumberList,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";

/** English strings. A translated content file overrides these through `ui`. */
export const MEAN_ABSOLUTE_DEVIATION_CALCULATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "3, 6, 6, 7, 8, 11, 15, 16",
  valuesDetected: "one: value detected|other: values detected",
  centerLabel: "Measure the deviations from",
  centerMean: "The mean (the usual definition)",
  centerMedian: "The median",
  tryExample: "Try an example",
  exampleData: "8, 3, 15, 6, 11, 7, 16, 6",
  clear: "Clear",
  enterPrompt: "Enter at least two numbers.",
  results: "Results",
  madMean: "Mean absolute deviation around the mean",
  madMedian: "Mean absolute deviation around the median",
  meanLabel: "Mean (x̄)",
  medianLabel: "Median",
  sdLabel: "Population SD, for comparison",
  tableTitle: "Deviation table",
  colX: "x",
  colDeviation: "x − x̄",
  colAbsMean: "|x − x̄|",
  colAbsMedian: "|x − median|",
  sum: "Sum",
  workingTitle: "How it was calculated",
  meanLine: "x̄ = Σx ÷ n = {sum} ÷ {n} = {mean}",
  madMeanLine: "Mean absolute deviation = Σ|x − x̄| ÷ n = {sum} ÷ {n} = {mad}",
  medianLine: "Median of the sorted data = {median}",
  madMedianLine: "Around the median = Σ|x − median| ÷ n = {sum} ÷ {n} = {mad}",
  zeroSumNote:
    "The signed deviations x − x̄ always add up to zero, which is why the absolute values are taken.",
  medianAdTitle: "Median absolute deviation — a different statistic",
  medianAd: "Median absolute deviation",
  scaledMedianAd: "Scaled median absolute deviation (× 1.4826)",
  medianAdLine:
    "Sorted |x − median|: {list} → their median = {mad}; × 1.4826 = {scaled}",
  listSeparator: ", ",
  moreValues: "… and {count} more",
  medianAdNote:
    "Also abbreviated MAD, but it takes the median of the absolute deviations instead of their mean. Scaled by 1.4826 it estimates the standard deviation of normally distributed data while ignoring outliers.",
} as const;

type Strings = typeof MEAN_ABSOLUTE_DEVIATION_CALCULATOR_STRINGS;
type Center = "mean" | "median";

/** A sum that is zero up to rounding noise is shown as zero. */
function cleanZero(value: number, scale: number): number {
  return Math.abs(value) < 1e-9 * (1 + scale) ? 0 : value;
}

function MedianAbsoluteDeviation({
  report,
  strings,
  fmt,
}: {
  report: DeviationReport;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const absolute = sorted(report.rows.map((row) => row.absFromMedian));
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.medianAdTitle}</h3>
      <StatGrid>
        <StatCard
          label={strings.medianAd}
          value={num(report.medianAbsoluteDeviation)}
        />
        <StatCard
          label={strings.scaledMedianAd}
          value={num(report.scaledMedianAbsoluteDeviation)}
        />
      </StatGrid>
      <Formula>
        {t(strings.medianAdLine, {
          list: formatNumberList(
            fmt,
            absolute,
            strings.listSeparator,
            strings.moreValues
          ),
          mad: num(report.medianAbsoluteDeviation),
          scaled: num(report.scaledMedianAbsoluteDeviation),
        })}
      </Formula>
      <Explanation>{strings.medianAdNote}</Explanation>
    </div>
  );
}

export default function MeanAbsoluteDeviationCalculatorWidget() {
  const strings = useToolUi(MEAN_ABSOLUTE_DEVIATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [center, setCenter] = useState<Center>("mean");

  const values = parseNumberList(raw);
  const report = values.length >= 2 ? deviationReport(values) : null;
  const num = (value: number) => formatStat(fmt, value);

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={
            <>
              {fmt(values.length)}
              {tPlural(strings.valuesDetected, locale, values.length)}
            </>
          }
          id="mad-data"
          label={strings.dataLabel}
          onChange={setRaw}
          placeholder={strings.dataPlaceholder}
          value={raw}
        />
        <PillGroup
          label={strings.centerLabel}
          name="mad-center"
          onChange={setCenter}
          options={[
            { value: "mean", label: strings.centerMean },
            { value: "median", label: strings.centerMedian },
          ]}
          value={center}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleData)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis={center === "mean"}
                label={strings.madMean}
                value={num(report.madAroundMean)}
              />
              <StatCard
                emphasis={center === "median"}
                label={strings.madMedian}
                value={num(report.madAroundMedian)}
              />
              <StatCard label={strings.meanLabel} value={num(report.mean)} />
              <StatCard
                label={strings.medianLabel}
                value={num(report.median)}
              />
              <StatCard
                label={strings.sdLabel}
                value={num(standardDeviation(values, true))}
              />
            </StatGrid>
          </div>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
            <ResultTable
              footer={[
                strings.sum,
                num(
                  cleanZero(
                    sum(report.rows.map((row) => row.fromMean)),
                    Math.abs(report.mean)
                  )
                ),
                num(report.sumAbsFromMean),
                num(report.sumAbsFromMedian),
              ]}
              head={[
                strings.colX,
                strings.colDeviation,
                strings.colAbsMean,
                strings.colAbsMedian,
              ]}
              rows={report.rows.map((row) => [
                num(row.x),
                num(row.fromMean),
                num(row.absFromMean),
                num(row.absFromMedian),
              ])}
            />
          </div>

          <WorkingPanel title={strings.workingTitle}>
            <Formula>
              {t(strings.meanLine, {
                sum: num(sum(values)),
                n: fmt(report.n),
                mean: num(report.mean),
              })}
            </Formula>
            <Formula>
              {t(strings.madMeanLine, {
                sum: num(report.sumAbsFromMean),
                n: fmt(report.n),
                mad: num(report.madAroundMean),
              })}
            </Formula>
            <Formula>
              {t(strings.medianLine, { median: num(report.median) })}
            </Formula>
            <Formula>
              {t(strings.madMedianLine, {
                sum: num(report.sumAbsFromMedian),
                n: fmt(report.n),
                mad: num(report.madAroundMedian),
              })}
            </Formula>
            <Explanation>{strings.zeroSumNote}</Explanation>
          </WorkingPanel>

          <MedianAbsoluteDeviation
            fmt={fmt}
            report={report}
            strings={strings}
          />
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
